import React, { useEffect, useState, useMemo } from 'react';
import { db, auth } from '../firebase/config';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const MAX_ATTEMPTS = 5;

const formatDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const NotificationsReport: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [all, setAll] = useState<any[]>([]);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [tokenRefreshAttempted, setTokenRefreshAttempted] = useState(false);
  const [checkingClaims, setCheckingClaims] = useState(false);
  const [tokenClaims, setTokenClaims] = useState<Record<string, any> | null>(null);
  const [lastTokenCheckedAt, setLastTokenCheckedAt] = useState<string | null>(null);
  const { reloadUser } = useAuth();
  const permLoggedRef = React.useRef(false);
  // Form state for creating push notifications
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState<'topic'|'tokens'|'broadcast'>('topic');
  const [topic, setTopic] = useState('');
  const [tokensText, setTokensText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'notifications'));
      const list = snap.docs.map(d => ({ id: d.id, ref: d.ref, ...(d.data() as any) }));
      setAll(list);
      const failed = list.filter(i => i.status === 'failed' && (i.attempts || 0) >= MAX_ATTEMPTS);
      setItems(failed);
      setPermissionsError(null);
    } catch (err: any) {
      // Log permission errors only once to avoid spamming the console
      if (err && (err.code === 'permission-denied' || String(err.message).includes('Missing or insufficient permissions'))) {
        if (!permLoggedRef.current) {
          console.warn('Erro ao buscar notificações (permission):', err);
          permLoggedRef.current = true;
        }
        // Detecta erro de permissão e tenta forçar refresh do token uma vez
        if (auth.currentUser && !tokenRefreshAttempted) {
          setTokenRefreshAttempted(true);
          try {
            await auth.currentUser.getIdToken(true);
            if (reloadUser) await reloadUser();
            // Re-tenta a busca após refresh
            await fetchAll();
            return;
          } catch (refreshErr) {
            console.warn('Refresh de token falhou', refreshErr);
          }
        }
        // Tenta ler claims atuais para ajudar no debug (não bloqueante)
        if (auth.currentUser) {
          auth.currentUser.getIdTokenResult().then(tr => {
            if (!permLoggedRef.current) console.warn('Claims atuais no cliente:', tr.claims);
            setTokenClaims(tr.claims || null);
            setLastTokenCheckedAt(new Date().toISOString());
          }).catch(e => { if (!permLoggedRef.current) console.warn('Não foi possível ler claims no cliente', e); });
        }
        setPermissionsError('Usuário sem permissão para acessar notificações. Verifique as regras do Firestore ou use uma Cloud Function com validação de administrador.');
      } else {
        if (!permLoggedRef.current) console.warn('Erro ao buscar notificações', err);
        setPermissionsError(null);
      }
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  const requeue = async (id: string) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { status: 'pending', attempts: 0, nextAttemptAt: new Date().toISOString(), error: '' });
      await fetchAll();
    } catch (err) { console.error(err); }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Remover notificação falha?')) return;
    try {
      await deleteDoc(doc(db, 'notifications', id));
      await fetchAll();
    } catch (err) { console.error(err); }
  };

  const resendNow = async (id: string) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { nextAttemptAt: new Date().toISOString(), status: 'pending' });
      await fetchAll();
    } catch (err) { console.error(err); }
  };

  const summary = useMemo(() => {
    const total = all.length;
    const failed = all.filter(i => i.status === 'failed').length;
    const pending = all.filter(i => i.status === 'pending').length;
    const avgAttempts = all.length ? (all.reduce((s, i) => s + (i.attempts || 0), 0) / all.length) : 0;
    const last24 = all.filter(i => i.updatedAt && new Date(i.updatedAt) > new Date(Date.now() - 24*60*60*1000)).length;
    return { total, failed, pending, avgAttempts: Number(avgAttempts.toFixed(2)), last24 };
  }, [all]);

  const trend = useMemo(() => {
    // last 7 days failures
    const days = 7;
    const buckets: Record<string, number> = {};
    for (let i = days-1; i >=0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      buckets[formatDate(d)] = 0;
    }
    all.forEach(i => {
      if (i.status === 'failed' && i.updatedAt) {
        const dt = new Date(i.updatedAt);
        const key = formatDate(dt);
        if (buckets[key] !== undefined) buckets[key]++;
      }
    });
    return Object.keys(buckets).map(k => ({ date: k, value: buckets[k] }));
  }, [all]);

  const exportCSV = () => {
    const rows = [['id','status','attempts','type','target','error','updatedAt']];
    all.forEach(i => {
      const type = i.fcmTopic ? 'fcm-topic' : (i.fcmBroadcast ? 'fcm-broadcast' : (i.tokens ? 'fcm-tokens' : (i.webhook ? 'webhook' : (i.to ? 'email' : 'other'))));
      const target = i.fcmTopic ? i.fcmTopic : (i.fcmBroadcast ? 'ALL_USERS' : (i.tokens ? (Array.isArray(i.tokens) ? i.tokens.slice(0,5).join(';') : String(i.tokens)) : (i.webhook || i.to || JSON.stringify(i.payload || {}))));
      rows.push([i.id, i.status, i.attempts || 0, type, target, (i.error||'').replace(/\n/g,' '), i.updatedAt || '']);
    });
    const csv = rows.map(r => r.map(c => '"'+String(c).replace(/"/g,'""')+'"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `notifications_export_${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="section-block">
      <h3>Monitor de Notificações</h3>
      <div style={{ display:'flex', gap: 12, marginBottom: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="admin-card">
            <div className="admin-card-title">Total</div>
            <div className="admin-card-value">{summary.total}</div>
          </div>
          <div className="admin-card">
            <div className="admin-card-title">Falhadas</div>
            <div className="admin-card-value">{summary.failed}</div>
          </div>
          <div className="admin-card">
            <div className="admin-card-title">Pendentes</div>
            <div className="admin-card-value">{summary.pending}</div>
          </div>
          <div className="admin-card">
            <div className="admin-card-title">Últimas 24h</div>
            <div className="admin-card-value">{summary.last24}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchAll} disabled={!!permissionsError}>Atualizar</button>
          <button onClick={exportCSV} className="primary" disabled={!!permissionsError}>Export CSV</button>
        </div>
      </div>

      {/* Form para envio de push notifications */}
      <div className="admin-card" style={{ marginBottom: 12 }}>
        <h4 style={{ marginTop: 0 }}>Enviar Push Notification</h4>
        {permissionsError && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffeeba', padding: 12, borderRadius: 8, marginBottom: 8 }}>
            <strong>Permissões insuficientes:</strong>
            <div style={{ marginTop: 6 }}>{permissionsError}</div>

            <div style={{ marginTop: 8 }}>
              <small>Exemplo de regra Firestore para permitir apenas administradores (use custom claims):</small>
              <pre style={{ background: '#f8f9fa', padding: 8, borderRadius: 6, overflowX: 'auto' }}>{`match /notifications/{docId} {
  allow read, write: if request.auth != null && request.auth.token.admin == true;
}`}</pre>

              <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => { navigator.clipboard.writeText("match /notifications/{docId} {\n  allow read, write: if request.auth != null && request.auth.token.admin == true;\n}"); alert('Regra copiada para área de transferência'); }}>Copiar regra</button>
                <button onClick={async () => {
                  try {
                    if (auth.currentUser) {
                      await auth.currentUser.getIdToken(true);
                      if (reloadUser) await reloadUser();
                      await fetchAll();
                      setPermissionsError(null);
                      alert('Token atualizado. Recarregando lista...');
                    } else {
                      alert('Faça logout/login e tente novamente.');
                    }
                  } catch (err) {
                    console.error('Erro ao atualizar token:', err);
                    alert('Não foi possível atualizar o token automaticamente. Faça logout/login.');
                  }
                }}>Atualizar permissões</button>

                <button onClick={async () => {
                  setCheckingClaims(true);
                  try {
                    if (!auth.currentUser) { alert('Nenhum usuário autenticado'); setCheckingClaims(false); return; }
                    const tr = await auth.currentUser.getIdTokenResult(true);
                    setTokenClaims(tr.claims || null);
                    setLastTokenCheckedAt(new Date().toISOString());
                    alert('Claims verificados; veja abaixo os detalhes');
                  } catch (err) {
                    console.error('Erro ao obter claims:', err);
                    alert('Não foi possível obter claims. Faça logout/login.');
                    setTokenClaims(null);
                  }
                  setCheckingClaims(false);
                }} disabled={checkingClaims}>{checkingClaims ? 'Verificando...' : 'Verificar claims'}</button>
              </div>

              {auth.currentUser && (
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <strong>UID:</strong> {auth.currentUser.uid}
                  <div style={{ marginTop: 6 }}>
                    <strong>Última verificação:</strong> {lastTokenCheckedAt || '—'}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <strong>Claims:</strong>
                    <pre style={{ background: '#fff', padding: 8, borderRadius: 6, overflowX: 'auto' }}>{tokenClaims ? JSON.stringify(tokenClaims, null, 2) : 'Nenhum claim disponível'}</pre>
                  </div>
                </div>
              )}

            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input aria-label="Título" placeholder="Título" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: '1 1 240px' }} />
          <input aria-label="Mensagem" placeholder="Mensagem" value={body} onChange={e => setBody(e.target.value)} style={{ flex: '2 1 420px' }} />
          <select aria-label="Alvo" value={targetType} onChange={e => setTargetType(e.target.value as any)}>
            <option value="topic">Tópico (topic)</option>
            <option value="tokens">Tokens (lista)</option>
            <option value="broadcast">Broadcast (todos os usuários)</option>
          </select>
        </div>
        <div style={{ marginTop: 8 }}>
          {targetType === 'topic' && (
            <input aria-label="Tópico" placeholder="Nome do tópico (ex: news)" value={topic} onChange={e => setTopic(e.target.value)} />
          )}
          {targetType === 'tokens' && (
            <textarea aria-label="Tokens" placeholder="Um token por linha ou separados por vírgula" rows={3} value={tokensText} onChange={e => setTokensText(e.target.value)} />
          )}
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
          <button onClick={async () => {
            if (!title || !body) { alert('Preencha título e mensagem'); return; }
            setSending(true);
            try {
              const payload: any = { title, body };
              if (targetType === 'topic') { payload.fcmTopic = topic || 'news'; }
              if (targetType === 'tokens') { payload.tokens = tokensText.split(/\n|,|;/).map(s => s.trim()).filter(Boolean); }
              if (targetType === 'broadcast') { payload.fcmBroadcast = true; }

              // Prefer callable function (server-side) to avoid client-side permission issues
              try {
                const functions = getFunctions();
                const enqueue = httpsCallable(functions, 'enqueueNotification');
                await enqueue(payload);
                setTitle(''); setBody(''); setTopic(''); setTokensText('');
                await fetchAll();
                alert('Notificação enfileirada com sucesso (via função)');
              } catch (callErr: any) {
                console.warn('Callable failed, trying direct write:', callErr);
                // If callable fails due to permission, try refresh token once and retry
                if (callErr && (callErr.code === 'permission-denied' || String(callErr.message).includes('Missing or insufficient permissions'))) {
                  if (auth.currentUser && !tokenRefreshAttempted) {
                    setTokenRefreshAttempted(true);
                    try {
                      await auth.currentUser.getIdToken(true);
                      if (reloadUser) await reloadUser();
                      // Retry callable once
                      const functions2 = getFunctions();
                      const enqueue2 = httpsCallable(functions2, 'enqueueNotification');
                      await enqueue2(payload);
                      setTitle(''); setBody(''); setTopic(''); setTokensText('');
                      await fetchAll();
                      alert('Notificação enfileirada com sucesso (via função após refresh)');
                      setSending(false);
                      return;
                    } catch (refreshErr) {
                      console.warn('Refresh de token falhou ao chamar função', refreshErr);
                    }
                  }
                  setPermissionsError('Usuário sem permissão para enfileirar via função. Verifique claims de admin.');
                  alert('Erro: permissão negada ao chamar função (verifique claims de admin).');
                } else {
                  // Fallback: try direct write to Firestore (may fail if security rules deny)
                  const payloadDoc: any = { ...payload, createdAt: new Date().toISOString(), status: 'pending', attempts: 0 };
                  await addDoc(collection(db, 'notifications'), payloadDoc as any);
                  setTitle(''); setBody(''); setTopic(''); setTokensText('');
                  await fetchAll();
                  alert('Notificação enfileirada com sucesso (fallback)');
                }
              }
            } catch (err: any) {
              console.error('Erro ao criar notificação', err);
              if (err && (err.code === 'permission-denied' || String(err.message).includes('Missing or insufficient permissions'))) {
                setPermissionsError('Usuário sem permissão para criar notificações. Ajuste as regras de segurança ou use uma Cloud Function autenticada como administrador.');
                alert('Erro: sem permissão para criar notificações. Verifique as regras do Firestore.');
              } else {
                alert('Erro ao enfileirar notificação');
              }
            }
            setSending(false);
          }} className="primary" disabled={sending || !!permissionsError}>{sending ? 'Enviando...' : 'Enviar'}</button>
          <button onClick={() => { setTitle(''); setBody(''); setTopic(''); setTokensText(''); }} disabled={!!permissionsError}>Limpar</button>
        </div>
      </div>

      <div style={{ width: '100%', height: 180, marginBottom: 12 }}>
        <ResponsiveContainer>
          <BarChart data={trend}>
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="#f97316" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {loading ? <p>Carregando...</p> : (
        items.length === 0 ? <p>Nenhuma notificação encontrada com MAX_ATTEMPTS.</p> : (
          <table className="admin-table compact">
            <thead>
              <tr>
                <th>ID</th>
                <th>Tipo</th>
                <th>Detalhes</th>
                <th>Attempt</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id}>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.id}</td>
                  <td>{i.fcmTopic ? `FCM (topic: ${i.fcmTopic})` : (i.fcmBroadcast ? 'FCM (broadcast)' : (i.tokens ? 'FCM (tokens)' : (i.webhook ? 'Webhook' : (i.to ? 'Email' : 'Outro'))))}</td>
                  <td style={{ maxWidth: 420 }}>{i.fcmTopic ? `topic: ${i.fcmTopic}` : (i.fcmBroadcast ? 'Todos os usuários' : (i.tokens ? `${(i.tokens||[]).slice(0,5).join(', ')}${(i.tokens && i.tokens.length>5 ? '...' : '')}` : (i.webhook ? i.webhook : (i.to ? `${i.to} • ${i.subject}` : JSON.stringify(i.payload || {})))) )}</td>
                  <td>{i.attempts}</td>
                  <td>
                    <button onClick={() => requeue(i.id)}>Reenfileirar</button>
                    <button onClick={() => resendNow(i.id)}>Reenviar agora</button>
                    <button onClick={() => remove(i.id)} style={{ background: '#fee2e2', borderColor: '#fecaca' }}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
};

export default NotificationsReport;